// src/pages/WorkspacesPage.tsx

import { useState } from 'react';
import {
  useGetWorkspacesQuery,
  useCreateWorkspaceMutation,
  useSwitchWorkspaceMutation,
} from '../api/workspacesApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';
import type { WorkspaceType, WorkspacePlan } from '../types/api';
import { HANDOFF_OIDC_CRM_ENABLED, CENTRAL_URL } from '../config/centralAuth';
import { beginRpWorkspaceTransition } from '../auth/rpWorkspaceTransition';
import { currentReturnTo } from '../auth/currentReturnTo';
import { useAppSelector } from '../store/store';
import { selectCreateWorkspaceUrl } from '../store/slices/bootContextSlice';

export default function WorkspacesPage() {
  const { data: workspaces, isLoading, error, refetch } = useGetWorkspacesQuery();
  const [createWorkspace, { isLoading: isCreating }] = useCreateWorkspaceMutation();
  const [switchWorkspace, { isLoading: isSwitching }] = useSwitchWorkspaceMutation();
  // Central's canonical create-workspace destination hint (from
  // /auth/context → meta.uiHints.createWorkspaceUrl). Single source of truth,
  // shared with the Suite Shell + WorkspaceSwitcher via `selectCreateWorkspaceUrl`.
  const createWorkspaceUrl = useAppSelector(selectCreateWorkspaceUrl);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWorkspace, setNewWorkspace] = useState({
    name: '',
    type: 'personal' as WorkspaceType,
    plan: 'free' as WorkspacePlan,
    industry: '',
  });

  // "Create New Workspace" entry point.
  //
  // RP/BFF (Central consumer contract, Central PR #478): CRM does NOT create the
  // workspace and does NOT call POST /api/workspaces. Central owns the ENTIRE
  // new-workspace lifecycle — create/reuse, onboarding, provisioning,
  // entitlements, default-app resolution, and launching into CRM (business) or
  // Home (personal). CRM's only responsibility is a top-level browser navigation
  // to Central's onboarding entry. No RP re-mint here (that is for SWITCHING an
  // EXISTING workspace).
  //
  // Destination source of truth: prefer Central's `createWorkspaceUrl` hint
  // (the same selector the Suite Shell + WorkspaceSwitcher use); fall back to
  // the documented `${CENTRAL_URL}/onboarding/new` entry only when the hint is
  // unavailable. `continue` is intentionally omitted so Central resolves the
  // final destination via its default-app logic.
  const handleCreateClick = () => {
    if (HANDOFF_OIDC_CRM_ENABLED) {
      const target =
        createWorkspaceUrl ?? (CENTRAL_URL ? `${CENTRAL_URL}/onboarding/new` : null);
      if (target) {
        window.location.assign(target);
      }
      return;
    }
    // Legacy Bearer mode: unchanged — open the in-app create modal below.
    setShowCreateModal(true);
  };

  // Legacy Bearer-only create flow. Retained unchanged for production (Bearer);
  // never reached in RP/BFF mode (the modal is only opened by handleCreateClick
  // when the handoff flag is OFF).
  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createWorkspace({
        name: newWorkspace.name,
        type: newWorkspace.type,
        plan: newWorkspace.plan,
        industry: newWorkspace.industry || undefined,
      }).unwrap();
      setShowCreateModal(false);
      setNewWorkspace({ name: '', type: 'personal', plan: 'free', industry: '' });
    } catch (error) {
      console.error('Failed to create workspace:', error);
    }
  };

  const handleSwitch = async (workspaceId: string) => {
    // RP/BFF: switching is a server-side session re-mint, not a browser-token
    // mutation. Reuse the same `/auth/start` transition (the legacy
    // `switchWorkspace` mutation returns a browser token that RP cannot use).
    if (HANDOFF_OIDC_CRM_ENABLED) {
      // Preserve the current safe local route so a successful re-mint returns
      // the user here rather than defaulting to `/`. Server-revalidated.
      beginRpWorkspaceTransition(workspaceId, currentReturnTo());
      return;
    }

    // Legacy Bearer mode: unchanged.
    try {
      await switchWorkspace(workspaceId).unwrap();
    } catch (error) {
      console.error('Failed to switch workspace:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message="Failed to load workspaces." onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Workspaces</h1>
        <Button onClick={handleCreateClick}>
          Create Workspace
        </Button>
      </div>

      {/* Workspace List */}
      <div className="space-y-3">
        {workspaces?.map((ws) => (
          <div
            key={ws.workspace.id}
            className={`bg-white rounded-xl p-4 shadow-sm border ${
              ws.isActive ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {ws.workspace.name}
                  </h3>
                  {ws.isActive && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                      Active
                    </span>
                  )}
                  {ws.isPrimary && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                      Primary
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1 capitalize">
                  {ws.workspace.type} · {ws.role}
                </p>
                {ws.workspace.industry && (
                  <p className="text-sm text-gray-400 mt-0.5">
                    Industry: {ws.workspace.industry}
                  </p>
                )}
              </div>
              {!ws.isActive && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleSwitch(ws.workspace.id)}
                  isLoading={isSwitching}
                >
                  Switch
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Create Workspace</h2>
            <form onSubmit={handleCreateWorkspace} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workspace Name
                </label>
                <input
                  type="text"
                  value={newWorkspace.name}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="My Workspace"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={newWorkspace.type}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, type: e.target.value as WorkspaceType })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="personal">Personal</option>
                  <option value="business">Business</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan
                </label>
                <select
                  value={newWorkspace.plan}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, plan: e.target.value as WorkspacePlan })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="free">Free</option>
                  <option value="standard">Standard</option>
                  <option value="ai">AI (with Credits)</option>
                </select>
              </div>

              {newWorkspace.type === 'business' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry (Optional)
                  </label>
                  <input
                    type="text"
                    value={newWorkspace.industry}
                    onChange={(e) => setNewWorkspace({ ...newWorkspace, industry: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Technology, Healthcare"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" isLoading={isCreating} className="flex-1">
                  Create
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
